/**
 * PDF Generation Engine for PrescriptionMakingPage
 *
 * Contains the client-side PDF generation functions extracted from index.jsx.
 * These functions are passed all component state as `deps` to avoid stale closures.
 */
import { toast } from 'react-toastify'
import { A4DpiToDetailsMap } from 'src/consumer/constants'

export async function generateClientSidePDFForPrint(
  selectedPages = null,
  autoPrint = false,
  _loadingToastId = null,
  deps = {}
) {
  const {
    loadingToastIdRef,
    printSettings,
    savePrescriptionData,
    setIsDownloadMode,
    setPdfBlob,
    setPdfDrawerOpen,
    setPdfGenerating,
    setPdfProgressMessage,
    setPdfUrl,
    setShouldAutoPrint,
  } = deps
  // This function generates PDF and opens it in the drawer for preview/printing
  // If autoPrint is true, it will skip the drawer and open print dialog directly
  
  console.log('[PDF Generation] Starting PDF generation', { selectedPages, autoPrint })
  
  // If autoPrint is true, open drawer with iframe and trigger print immediately
  if (autoPrint) {
    console.log('[PDF Generation] Auto-print mode: opening drawer with iframe, triggering print')
    
    // Set loading state and open drawer immediately
    setPdfUrl((oldPdfUrl) => {
      if (oldPdfUrl) {
        try {
          URL.revokeObjectURL(oldPdfUrl)
        } catch (revokeError) {
          console.warn('Error revoking old PDF URL:', revokeError)
        }
      }
      return null
    })
    setPdfBlob(null)
    setPdfGenerating(true)
    setPdfProgressMessage('Preparing PDF generation...')
    setShouldAutoPrint(true) // Enable auto-print
    setIsDownloadMode(false)
    setPdfDrawerOpen(true) // Open drawer so iframe can load
    console.log('[PDF Generation] Drawer opened for auto-print mode')
    
    // Continue with normal PDF generation flow - the useEffect will handle auto-print
    // Don't return early, let it generate PDF and set URL, then auto-print will trigger
  }
  
  // Preview mode: open drawer and show PDF
  // Set loading state and open drawer immediately to show progress
  // Revoke previous PDF URL to prevent memory leaks
  setPdfUrl((oldPdfUrl) => {
    if (oldPdfUrl) {
      try {
        URL.revokeObjectURL(oldPdfUrl)
      } catch (revokeError) {
        console.warn('Error revoking old PDF URL:', revokeError)
      }
    }
    return null
  })
  setPdfBlob(null) // Clear any previous PDF blob
  setPdfGenerating(true)
  setPdfProgressMessage('Preparing PDF generation...')
  setShouldAutoPrint(false) // No auto-print for preview mode
  console.log('[PDF Generation] Setting shouldAutoPrint: false (preview mode)')
  // Set download mode: false for both print and preview (we want to show PDF in drawer)
  // isDownloadMode is only true when explicitly downloading from server
  setIsDownloadMode(false) // Always false for client-side PDF generation (both print and preview)
  setPdfDrawerOpen(true) // Open drawer immediately to show loading state and progress
  console.log('[PDF Generation] Drawer opened immediately, isDownloadMode:', false)
  
  console.log('[PDF Generation] Drawer opened, PDF generation started (preview mode)')
  
  try {
    // Save prescription data asynchronously in background (non-blocking)
    // This ensures data is saved but doesn't block PDF generation
    // Start save in background - don't await it, let it run completely async
    // Suppress toast since this is a background save during PDF generation
    savePrescriptionData(true, true).catch(err => {
      console.warn('Background save failed during preview/print:', err)
      // Don't show error toast - save is in background
    })
    
    // Note: Patient history notes are saved by savePrescriptionData() above
    // No need to call saveCurrentNotes() separately to avoid duplicate API calls
    
    setPdfProgressMessage('Analyzing prescription pages...')
    
    // Get A4 dimensions from printSettings
    const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
    const heightOfA4Page = currentDpiData?.height || 1123
    const widthOfA4Page = currentDpiData?.width || 794
    
    // Get all prescription pages - avoid duplicates when both main page and Rx drawer are rendered
    // When Rx drawer is open, both main page and drawer have pages, causing duplicates
    // Strategy: Only use pages from visible containers, and deduplicate by page number
    
    let allPages = Array.from(document.querySelectorAll('.prescription-page'))
    
    // Filter out pages from hidden containers
    allPages = allPages.filter(page => {
      // Check if page itself is visible
      const pageStyle = window.getComputedStyle(page)
      if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
        return false
      }
      
      // Check if page is in a visible container (not hidden drawer/modal)
      let parent = page.parentElement
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent)
        // Skip if parent is hidden
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false
        }
        // Skip if parent is a closed drawer (MUI Drawer with display: none)
        if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
          return false
        }
        parent = parent.parentElement
      }
      return true
    })
    
    // Deduplicate by page number - if multiple pages have same page number, keep only one
    // This handles the case where both main page and Rx drawer have pages with same numbers
    const pagesByNumber = new Map()
    allPages.forEach(page => {
      const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
      if (pageNum) {
        // If we already have a page with this number, prefer the one not in PMPPrintWrapper
        // (main page takes priority over Rx drawer)
        if (!pagesByNumber.has(pageNum)) {
          pagesByNumber.set(pageNum, page)
        } else {
          const existingPage = pagesByNumber.get(pageNum)
          const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
          const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
          
          // Prefer main page over drawer page
          if (isExistingInDrawer && !isCurrentInDrawer) {
            pagesByNumber.set(pageNum, page)
          }
        }
      } else {
        // If no page number, add it (shouldn't happen, but handle it)
        pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
      }
    })
    
    allPages = Array.from(pagesByNumber.values())
    
    // Sort pages by data-page-number to ensure correct order
    allPages.sort((a, b) => {
      const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
      const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
      return pageNumA - pageNumB
    })
    
    // Debug: Log page details
    console.log(`[PDF Generation] Found ${allPages.length} unique page(s) to process`, 
      allPages.map((p, idx) => ({
        index: idx,
        pageNumber: p.getAttribute('data-page-number') || p.getAttribute('data-pdf-page'),
        isVisible: window.getComputedStyle(p).display !== 'none',
        container: p.closest('.PMPPrintWrapper') ? 'Rx Drawer' : 'Main Page'
      }))
    )
    
    // Enhanced debug logging (disabled to reduce console noise)
    // Uncomment if debugging PDF generation issues:
    // console.log(`[generateClientSidePDFForPrint] Found ${allPages.length} pages:`, 
    //   allPages.map((p, idx) => {
    //     const pageNum = p.getAttribute('data-page-number')
    //     const hasRxTable = p.querySelector('table') !== null
    //     const hasRxChunk = p.textContent.includes('Rx') || p.textContent.includes('contd')
    //     const textLength = p.textContent.trim().length
    //     return {
    //       index: idx,
    //       pageNumber: pageNum,
    //       hasRxTable,
    //       hasRxChunk,
    //       textLength,
    //       className: p.className,
    //       isPatientHistory: p.classList.contains('patient-history-notes-container')
    //     }
    //   })
    // )
    
    // Filter pages if specific pages are selected
    if (selectedPages && selectedPages.length > 0) {
      allPages = allPages.filter((page, idx) => {
        const pageNum = parseInt(page.getAttribute('data-page-number')) || (idx + 1)
        return selectedPages.includes(pageNum)
      })
    }
    
    if (!allPages || allPages.length === 0) {
      console.error('[PDF Generation] No prescription pages found')
      setPdfGenerating(false)
      setPdfProgressMessage('No prescription pages found to print')
      // Keep drawer open to show error message
      setPdfDrawerOpen(true) // Ensure drawer is open to show error
      setIsDownloadMode(false) // Reset download mode on error
      toast.error('No prescription pages found to print', { autoClose: false })
      return
    }
    
    console.log(`[PDF Generation] Found ${allPages.length} page(s) to process`)
    
    // Debug logging moved above after sorting

    setPdfProgressMessage(`Found ${allPages.length} page${allPages.length > 1 ? 's' : ''}. Processing content...`)

    // Create a container for all pages
    const pdfContainer = document.createElement('div')
    pdfContainer.style.width = `${widthOfA4Page}px`
    pdfContainer.style.margin = '0'
    pdfContainer.style.padding = '0'
    pdfContainer.style.backgroundColor = '#fff'
    pdfContainer.style.display = 'block'
    pdfContainer.style.position = 'relative'
    
    // Track created elements for cleanup to prevent memory leaks
    const createdElements = []
    
    // Helper function to convert image to base64 - prioritizes already-loaded images
    const imageToBase64 = async (url, originalImgElement = null) => {
      // Strategy 1: Use already-loaded image from DOM if available and safe
      if (originalImgElement && originalImgElement.complete && originalImgElement.naturalHeight !== 0) {
        try {
          // Check if image is from same origin or was loaded with CORS
          const imgUrl = new URL(originalImgElement.src, window.location.href)
          const isSameOrigin = imgUrl.origin === window.location.origin
          
          // Try to get from canvas if same origin or if crossOrigin was set
          if (isSameOrigin || originalImgElement.crossOrigin !== null) {
            const canvas = document.createElement('canvas')
            canvas.width = originalImgElement.naturalWidth || originalImgElement.width
            canvas.height = originalImgElement.naturalHeight || originalImgElement.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(originalImgElement, 0, 0)
            return canvas.toDataURL('image/png')
          }
        } catch (e) {
          // Canvas might be tainted, continue to other methods
          console.warn('Could not get image from DOM element (tainted canvas), trying other methods:', url)
        }
      }
      
      // Strategy 2: Try to get from any canvas that might have rendered this image
      try {
        const allCanvases = document.querySelectorAll('canvas')
        for (const canvas of allCanvases) {
          try {
            // Check if this canvas might contain our image
            const imageData = canvas.toDataURL('image/png')
            if (imageData && imageData !== 'data:,') {
              // This is a heuristic - if canvas has content, it might be our image
              // For now, skip this strategy as it's not reliable
            }
          } catch (e) {
            // Canvas is tainted, skip
            continue
          }
        }
      } catch (e) {
        // Ignore canvas search errors
      }
      
      // Strategy 3: Fetch with CORS headers (respects server CORS settings)
      try {
        const response = await fetch(url, { 
          mode: 'cors', 
          credentials: 'omit',
          headers: {
            'Accept': 'image/*',
          }
        })
        if (response.ok) {
          const blob = await response.blob()
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            createdElements.push(reader) // Track for cleanup
            reader.onloadend = () => {
              resolve(reader.result)
              // Clean up reader
              reader.onloadend = null
              reader.onerror = null
            }
            reader.onerror = () => {
              reject(new Error('FileReader failed'))
              reader.onloadend = null
              reader.onerror = null
            }
            reader.readAsDataURL(blob)
          })
        }
      } catch (fetchError) {
        // Fetch failed, try Image element approach
        console.warn('Fetch with CORS failed, trying Image element:', url, fetchError.message)
      }
      
      // Strategy 4: Use Image element with same crossOrigin as original (if available)
      return new Promise((resolve) => {
        const img = new Image()
        createdElements.push(img) // Track for cleanup
        // Use same crossOrigin setting as original image if available
        if (originalImgElement && originalImgElement.crossOrigin !== null) {
          img.crossOrigin = originalImgElement.crossOrigin
        } else {
          // Default to anonymous for cross-origin images
          try {
            const imgUrl = new URL(url, window.location.href)
            if (imgUrl.origin !== window.location.origin) {
              img.crossOrigin = 'anonymous'
            }
          } catch (e) {
            // URL parsing failed, try anonymous anyway
            img.crossOrigin = 'anonymous'
          }
        }
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            createdElements.push(canvas) // Track for cleanup
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            const dataUrl = canvas.toDataURL('image/png')
            // Clean up canvas immediately after use
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            canvas.width = 0
            canvas.height = 0
            resolve(dataUrl)
          } catch (e) {
            // Canvas is tainted, return original URL (html2canvas will handle with allowTaint)
            console.warn('Canvas tainted after Image load, will use original URL with allowTaint:', url)
            resolve(url)
          }
        }
        img.onerror = () => {
          // Image failed to load, return original URL
          console.warn('Image load failed, will use original URL:', url)
          resolve(url)
        }
        img.src = url
      })
    }

    // Clone each page and add to container (same logic as generateClientSidePDF)
    for (let idx = 0; idx < allPages.length; idx++) {
      const page = allPages[idx]
      const clonedPage = page.cloneNode(true)
      
      const hideElements = clonedPage.querySelectorAll('.hide-on-print-only, .hide-on-print')
      hideElements.forEach(el => el.remove())
      
      // Convert images to base64 - prioritize already-loaded images
      const images = clonedPage.querySelectorAll('img')
      for (const clonedImg of images) {
        const src = clonedImg.getAttribute('src')
        if (src && !src.startsWith('data:')) {
          try {
            // Try to find the original image element in the DOM (more robust search)
            let originalImg = null
            try {
              // First try exact src match
              originalImg = page.querySelector(`img[src="${src}"]`)
              if (!originalImg) {
                // Try finding by comparing src (handles relative/absolute URL differences)
                const allImgs = Array.from(page.querySelectorAll('img'))
                originalImg = allImgs.find(img => {
                  try {
                    return img.src === src || 
                           img.getAttribute('src') === src ||
                           new URL(img.src, window.location.href).href === new URL(src, window.location.href).href
                  } catch (e) {
                    return img.src === src || img.getAttribute('src') === src
                  }
                })
              }
            } catch (e) {
              console.warn('Error finding original image element:', e)
            }
            
            const base64 = await imageToBase64(src, originalImg)
            if (base64 && base64.startsWith('data:')) {
              clonedImg.src = base64
            } else {
              // If conversion failed, keep original URL - browser may still be able to print it
              console.warn('Image conversion returned non-base64, keeping original URL:', src)
              // Don't remove - keep the original src for browser printing
            }
          } catch (error) {
            console.error('Failed to convert image to base64:', src, error)
            // Keep original URL instead of removing - browser might still load it for printing
            console.warn('Keeping original image URL despite error:', src)
          }
        }
      }
      
      // Handle background images
      const elementsWithBgImages = clonedPage.querySelectorAll('[style*="background-image"]')
      for (const el of elementsWithBgImages) {
        const style = el.getAttribute('style')
        const bgMatch = style.match(/background-image:\s*url\(['"]?([^'"]+)['"]?\)/)
        if (bgMatch && bgMatch[1] && !bgMatch[1].startsWith('data:')) {
          try {
            const base64 = await imageToBase64(bgMatch[1])
            el.style.backgroundImage = `url(${base64})`
          } catch (error) {
            console.warn('Failed to convert background image to base64:', bgMatch[1], error)
          }
        }
      }
      
      // Style the cloned page
      clonedPage.style.width = 'auto'
      clonedPage.style.minWidth = `${widthOfA4Page}px`
      clonedPage.style.maxWidth = 'none'
      clonedPage.style.height = 'auto' // Let content determine height
      clonedPage.style.minHeight = `${heightOfA4Page}px`
      clonedPage.style.maxHeight = 'none' // Don't constrain height - allow overflow
      clonedPage.style.margin = '0'
      clonedPage.style.marginBottom = '0'
      clonedPage.style.padding = '0'
      clonedPage.style.display = 'block'
      clonedPage.style.position = 'relative'
      clonedPage.style.boxSizing = 'border-box'
      clonedPage.style.overflow = 'visible'
      
      // Remove width constraints from children
      const allChildren = clonedPage.querySelectorAll('*')
      allChildren.forEach(child => {
        const childStyle = window.getComputedStyle(child)
        if (childStyle.maxWidth && childStyle.maxWidth !== 'none') {
          child.style.maxWidth = 'none'
        }
        if (child.scrollWidth > widthOfA4Page) {
          child.style.width = 'auto'
          child.style.maxWidth = 'none'
        }
      })
      
      clonedPage.setAttribute('data-pdf-page', idx + 1)
      pdfContainer.appendChild(clonedPage)
    }

    setPdfProgressMessage('Preparing pages for PDF conversion...')
    
    // Create temporary container
    const tempContainer = document.createElement('div')
    tempContainer.className = 'temp-pdf-container'
    tempContainer.style.position = 'absolute'
    tempContainer.style.left = '-9999px'
    tempContainer.style.top = '0'
    tempContainer.style.width = `${widthOfA4Page}px`
    tempContainer.style.backgroundColor = '#fff'
    tempContainer.appendChild(pdfContainer)
    document.body.appendChild(tempContainer)

    // Pages are already rendered, no delay needed
    setPdfProgressMessage('Loading PDF library...')
    
    // Generate PDF (same logic as generateClientSidePDF)
    const { jsPDF } = await import('jspdf')
    // Use 'a4' format instead of custom pixel dimensions to avoid scaling issues
    const pdf = new jsPDF({
      unit: 'px',
      format: 'a4',
      orientation: 'portrait',
      compress: true,
    })
    
    // Get the actual PDF page dimensions in pixels (jsPDF converts a4 to pixels internally)
    const _pdfPageWidth = pdf.internal.pageSize.getWidth()
    const _pdfPageHeight = pdf.internal.pageSize.getHeight()
    
    setPdfProgressMessage(`Converting pages to PDF (0/${allPages.length})...`)
    // Debug logging (disabled to reduce console noise)
    // console.log('[PDF Generation] Starting to process', allPages.length, 'pages')
    
    let pdfPageCount = 0
    for (let idx = 0; idx < allPages.length; idx++) {
      // Debug logging (disabled to reduce console noise)
      // console.log(`[PDF Generation] Processing page ${idx + 1}/${allPages.length}`)
      const pageElement = pdfContainer.children[idx]
      if (!pageElement) continue
      
      // Update progress message
      setPdfProgressMessage(`Converting page ${idx + 1} of ${allPages.length}...`)
      
      // Check if page has actual content (not just whitespace)
      // Be more lenient - check for tables, images, or any visible content
      // This is important for Rx chunks which might have minimal text but contain tables
      const hasTextContent = pageElement.textContent.trim().length > 0
      const hasImages = pageElement.querySelectorAll('img, canvas, svg').length > 0
      const hasTables = pageElement.querySelectorAll('table').length > 0
      const hasContent = hasTextContent || hasImages || hasTables
      
      if (!hasContent) {
        // Debug logging (disabled to reduce console noise)
        // const pageNumber = pageElement.getAttribute('data-pdf-page') || (idx + 1)
        // console.log(`[PDF Generation] Skipping empty page ${idx + 1} (PDF page ${pageNumber})`)
        // console.log(`  - textContent length: ${pageElement.textContent.trim().length}`)
        // console.log(`  - images: ${pageElement.querySelectorAll('img, canvas, svg').length}`)
        // console.log(`  - tables: ${pageElement.querySelectorAll('table').length}`)
        continue
      }
      
      // Debug logging (disabled to reduce console noise)
      // const pageNumber = pageElement.getAttribute('data-pdf-page') || (idx + 1)
      // const hasRxTable = pageElement.querySelector('table') !== null
      // console.log(`[PDF Generation] Processing page ${idx + 1}/${allPages.length} (PDF page ${pageNumber}) - hasRxTable: ${hasRxTable}`)
      
      // Add new page to PDF (except for the very first page added)
      if (pdfPageCount > 0) {
        pdf.addPage()
      }
      pdfPageCount++
      
      // Minimal delay - pages are already rendered
      // await new Promise(resolve => setTimeout(resolve, 100))
      
      // OPTIMIZATION: Batch all layout reads together to avoid forced reflows
      // Read all layout properties in one batch before any writes
      const pageRect = pageElement.getBoundingClientRect()
      const scrollWidth = pageElement.scrollWidth || pageRect.width
      const scrollHeight = pageElement.scrollHeight || pageRect.height
      
      // Batch child measurements - collect all children first, then measure all at once
      const allChildren = Array.from(pageElement.querySelectorAll('*'))
      const childMeasurements = allChildren.map(child => {
        const childRect = child.getBoundingClientRect()
        return {
          scrollWidth: child.scrollWidth || childRect.width,
          width: childRect.width
        }
      })
      
      // Find max width from cached measurements (no additional layout reads)
      let maxChildWidth = scrollWidth
      childMeasurements.forEach(measurement => {
        if (measurement.scrollWidth > maxChildWidth) {
          maxChildWidth = measurement.scrollWidth
        }
      })
      
      const captureWidth = Math.max(maxChildWidth, scrollWidth, widthOfA4Page)
      // Use exact A4 height - don't capture beyond page bounds to avoid footer cutoff
      // For patient history notes, we'll capture each page separately to avoid text cutting
      const isPatientHistoryNotes = pageElement.classList.contains('patient-history-notes-container')
      
      // Get the actual PDF page dimensions early (jsPDF converts a4 to pixels internally)
      const _pdfPageWidthInner = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      
      // Use scrollHeight to capture all content, but for non-patient-history pages, cap at PDF page height
      // This ensures we capture all content including boxes at the bottom
      const _captureHeight = isPatientHistoryNotes ? scrollHeight : Math.min(scrollHeight, pdfPageHeight)
      
      const originalWidth = pageElement.style.width
      const originalMaxWidth = pageElement.style.maxWidth
      pageElement.style.width = `${captureWidth}px`
      pageElement.style.maxWidth = 'none'
      
      try {
        const html2canvas = (await import('html2canvas')).default
        // Get canvas scale from printSettings, default to 1.0 for regular pages (sufficient for print), 1.0 for patient history
        // Reduced from 1.5 to 1.0 to significantly reduce file size while maintaining good print quality
        const defaultScale = isPatientHistoryNotes ? 1.0 : 1.0
        const canvasScale = printSettings?.canvasScale || defaultScale
        
        // For patient history notes that span multiple pages, capture each page separately
        if (isPatientHistoryNotes && scrollHeight > pdfPageHeight) {
          
          const pagesNeeded = Math.ceil(scrollHeight / pdfPageHeight)
          
          // Find the content wrapper to adjust position for each page
          const contentWrapper = pageElement.querySelector('.patient-history-notes-content-wrapper')
          if (!contentWrapper) {
            console.warn('Patient history notes content wrapper not found, falling back to single page capture')
            // Fall through to single page capture below
          } else {
            const originalTransform = contentWrapper.style.transform || ''
            const originalOverflow = pageElement.style.overflow || ''
            const originalHeight = pageElement.style.height || ''
            
            // Set container to show only one page at a time (use PDF page height)
            pageElement.style.overflow = 'hidden'
            pageElement.style.height = `${pdfPageHeight}px`
            
            for (let pageIdx = 0; pageIdx < pagesNeeded; pageIdx++) {
              // Add new PDF page (except for the very first page)
              if (pdfPageCount > 0) {
                pdf.addPage()
              }
              pdfPageCount++
              
              // Calculate the Y offset to show the correct portion of content (use PDF page height)
              const contentOffsetY = -pageIdx * pdfPageHeight
              
              // Adjust content position to show this page's content
              contentWrapper.style.transform = `translateY(${contentOffsetY}px)`
              
              // Wait a moment for the transform to apply
              await new Promise(resolve => setTimeout(resolve, 10))
              
              // Capture this page portion (use PDF page height)
              const canvas = await html2canvas(pageElement, {
                scale: canvasScale,
                useCORS: false,
                logging: false,
                letterRendering: true,
                allowTaint: false,
                backgroundColor: '#ffffff',
                width: captureWidth,
                height: pdfPageHeight,
                windowWidth: captureWidth,
                windowHeight: pdfPageHeight,
                x: 0,
                y: 0,
                scrollX: 0,
                scrollY: 0,
                removeContainer: false,
                onclone: null,
              })
              
              // Calculate scaling for PDF
              const canvasActualWidth = canvas.width / canvasScale
              const canvasActualHeight = canvas.height / canvasScale
              
              // Scale to fit PDF page dimensions exactly (same logic as regular pages)
              const scaleX = _pdfPageWidthInner / canvasActualWidth
              const scaleY = pdfPageHeight / canvasActualHeight
              // Use the smaller scale to ensure it fits both dimensions
              const scale = Math.min(scaleX, scaleY)
              
              // Final dimensions for PDF (use consistent scaling, not separate width/height scaling)
              const finalWidth = canvasActualWidth * scale
              const finalHeight = canvasActualHeight * scale
              
              // Optimized quality (0.85) for good balance between file size and print quality
              const imgData = canvas.toDataURL('image/jpeg', 0.85)
              pdf.addImage(imgData, 'JPEG', 0, 0, finalWidth, finalHeight, undefined, 'FAST')
              
              // Clean up canvas immediately after use to free memory
              const ctx = canvas.getContext('2d')
              if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height)
              }
              canvas.width = 0
              canvas.height = 0
            }
            
            // Restore original styles
            contentWrapper.style.transform = originalTransform
            pageElement.style.overflow = originalOverflow
            pageElement.style.height = originalHeight
          }
        }
        
        // Single page or non-patient-history-notes or fallback - capture normally
        if (!isPatientHistoryNotes || scrollHeight <= pdfPageHeight || !pageElement.querySelector('.patient-history-notes-content-wrapper')) {
          // Single page or non-patient-history-notes - capture normally
          const canvas = await html2canvas(pageElement, {
            scale: canvasScale,
            useCORS: false, // Don't re-fetch images, use already loaded ones
            logging: false,
            letterRendering: true,
            allowTaint: false, // Don't allow taint - images are already base64
            backgroundColor: '#ffffff',
            width: captureWidth,
            height: scrollHeight, // Use scrollHeight to capture all content
            windowWidth: captureWidth,
            windowHeight: scrollHeight, // Use scrollHeight to ensure all content is captured
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            removeContainer: false,
            onclone: (clonedDoc) => {
              // Ensure all elements are visible in the cloned document
              const clonedPage = clonedDoc.querySelector('.prescription-page')
              if (clonedPage) {
                // Make sure flex containers and boxes are visible
                const flexContainers = clonedPage.querySelectorAll('[style*="flex"]')
                flexContainers.forEach(container => {
                  if (container.style.display === 'none') {
                    container.style.display = ''
                  }
                })
                // Ensure continued boxes are visible
                const continuedBoxes = clonedPage.querySelectorAll('[id*="continued-box"]')
                continuedBoxes.forEach(box => {
                  box.style.display = 'flex'
                  box.style.visibility = 'visible'
                  box.style.opacity = '1'
                })
              }
            },
          })
          
          // Adjust for canvas scale
          const canvasActualWidth = canvas.width / canvasScale
          const canvasActualHeight = canvas.height / canvasScale
          
          // Get the actual PDF page dimensions (jsPDF converts a4 to pixels internally)
          const pdfPageWidth = pdf.internal.pageSize.getWidth()
          const pdfPageHeight = pdf.internal.pageSize.getHeight()
          
          // Single page - normal processing
          const scaleX = pdfPageWidth / canvasActualWidth
          const scaleY = pdfPageHeight / canvasActualHeight
          const scale = Math.min(scaleX, scaleY)
          
          const finalWidth = canvasActualWidth * scale
          const finalHeight = canvasActualHeight * scale
          
          // Slightly reduced quality (0.95 vs 0.98) for faster processing, still excellent print quality
          const imgData = canvas.toDataURL('image/jpeg', 0.95)
          pdf.addImage(imgData, 'JPEG', 0, 0, finalWidth, finalHeight, undefined, 'FAST')
          
          // Clean up canvas immediately after use to free memory
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
          }
          canvas.width = 0
          canvas.height = 0
        }
        
        pageElement.style.width = originalWidth
        pageElement.style.maxWidth = originalMaxWidth
      } catch (error) {
        // Restore styles even on error
        pageElement.style.width = originalWidth
        pageElement.style.maxWidth = originalMaxWidth
        // Log error but don't throw - continue with other pages
        console.error(`Error processing page ${idx + 1} (${isPatientHistoryNotes ? 'patient history notes' : 'regular page'}):`, error)
        setPdfProgressMessage(`Error processing page ${idx + 1}, continuing...`)
        // Continue to next page instead of failing completely
        continue
      }
    }
    
    // Debug logging (disabled to reduce console noise)
    // console.log('[PDF Generation] Finished processing all pages. Total PDF pages:', pdfPageCount)
    // console.log('[PDF Generation] Creating PDF blob...')
    
    // Get PDF as blob and set it in state to show in drawer
    let pdfBlobForDrawer
    let pdfUrlForDrawer
    try {
      pdfBlobForDrawer = pdf.output('blob')
      console.log('[PDF Generation] PDF blob created, size:', pdfBlobForDrawer.size, 'bytes')
      pdfUrlForDrawer = URL.createObjectURL(pdfBlobForDrawer)
      console.log('[PDF Generation] PDF blob URL created:', pdfUrlForDrawer)
      
      // Store blob for download functionality
      setPdfBlob(pdfBlobForDrawer)
    } catch (pdfError) {
      console.error('[PDF Generation] Error creating PDF blob:', pdfError)
      throw new Error(`Failed to create PDF blob: ${pdfError.message}`)
    }
    
    // Cleanup temp container and all created elements before opening drawer
    try {
      // Clean up all created canvas and image elements
      createdElements.forEach(element => {
        try {
          if (element instanceof HTMLCanvasElement) {
            // Clear canvas
            const ctx = element.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, element.width, element.height)
            }
            element.width = 0
            element.height = 0
          } else if (element instanceof HTMLImageElement) {
            // Remove image src to free memory
            element.src = ''
            element.onload = null
            element.onerror = null
          } else if (element instanceof FileReader) {
            // Clean up FileReader
            element.onloadend = null
            element.onerror = null
          }
          // Remove from DOM if it was added
          if (element.parentNode) {
            element.parentNode.removeChild(element)
          }
        } catch (cleanupErr) {
          // Ignore cleanup errors for individual elements
        }
      })
      createdElements.length = 0 // Clear array
      
      // Remove temp container and all its children (including any canvases)
      if (tempContainer && tempContainer.parentNode) {
        // Remove all children first to ensure canvases are cleaned up
        while (tempContainer.firstChild) {
          const child = tempContainer.firstChild
          // Clean up any canvases in the container
          if (child instanceof HTMLCanvasElement) {
            const ctx = child.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, child.width, child.height)
            }
            child.width = 0
            child.height = 0
          }
          tempContainer.removeChild(child)
        }
        document.body.removeChild(tempContainer)
      }
    } catch (cleanupError) {
      console.warn('Error cleaning up temp container:', cleanupError)
    }
    
    setPdfProgressMessage('Finalizing PDF...')
    
    // PDF is ready - set URL and open drawer
    console.log('[PDF Generation] PDF blob created, setting URL and opening drawer', { 
      pdfUrl: pdfUrlForDrawer, 
      autoPrint
    })
    
    // CRITICAL: Set PDF URL first, then open drawer, then clear generating flag
    // Revoke old PDF URL before setting new one to prevent memory leaks
    setPdfUrl((oldPdfUrl) => {
      if (oldPdfUrl) {
        try {
          window.URL.revokeObjectURL(oldPdfUrl)
        } catch (revokeError) {
          console.warn('Error revoking old PDF URL:', revokeError)
        }
      }
      return pdfUrlForDrawer
    })
    
    // Ensure drawer is open to show PDF
    // Set drawer open immediately (don't wait for requestAnimationFrame)
    setPdfDrawerOpen(true)
    console.log('[PDF Generation] Drawer state set to open immediately', { autoPrint, pdfUrl: pdfUrlForDrawer })
    
    // Use requestAnimationFrame to ensure state updates are batched properly
    requestAnimationFrame(() => {
      // Double-check drawer is open
      setPdfDrawerOpen(true)
      console.log('[PDF Generation] Drawer state confirmed open in requestAnimationFrame')
      
      // Wait a moment for the PDF URL and drawer state to be set before clearing generating flag
    // This prevents race conditions where the drawer might close
    setTimeout(() => {
        console.log('[PDF Generation] PDF generation complete, clearing generating flag', {
          pdfDrawerOpen: true,
          pdfUrl: pdfUrlForDrawer ? 'set' : 'null',
          autoPrint,
          pdfGenerating: false,
          shouldAutoPrint: autoPrint
        })
      setPdfGenerating(false)
      setPdfProgressMessage('')
      // Ensure drawer is still open after state updates
      setPdfDrawerOpen(true)
        // Ensure shouldAutoPrint is set if autoPrint was true
      if (autoPrint) {
          setShouldAutoPrint(true)
          // Don't dismiss loading toast here - let it be dismissed when print dialog opens
          // The loading toast will be dismissed by the useEffect when print is triggered
      } else {
          setShouldAutoPrint(false)
          // Dismiss loading toast for preview mode
          // Update loading toast to error
          const toastId = loadingToastIdRef.current
          if (toastId) {
            toast.update(toastId, {
              render: 'Error opening print dialog',
              type: 'error',
              isLoading: false,
              autoClose: 3000,
            })
            loadingToastIdRef.current = null
          }
          toast.success('PDF ready for preview!', { autoClose: 2000 })
      }
    }, 100)
    })
    
    // Save is running completely async in background - no need to wait for it
    // The savePromise will complete on its own, errors are already handled above
    
  } catch (error) {
    console.error('[PDF Generation] Error generating PDF for print:', error)
    console.error('[PDF Generation] Error stack:', error.stack)
    setPdfGenerating(false)
    setPdfProgressMessage(`Error: ${error.message || 'Failed to generate PDF'}`)
    setPdfDrawerOpen(true) // Ensure drawer is open to show error message
    setIsDownloadMode(false) // Reset download mode on error
    setPdfBlob(null) // Clear blob on error
    // Dismiss loading toast on error
    if (loadingToastIdRef.current) {
                        const toastId = loadingToastIdRef.current
                        if (toastId) {
                          toast.update(toastId, {
                            render: 'Opening print dialog...',
                            type: 'success',
                            isLoading: false,
                            autoClose: 2000,
                          })
                          loadingToastIdRef.current = null
                        }
    }
    toast.error(`Failed to generate PDF: ${error.message || 'Unknown error'}`, { autoClose: 5000 })
    
    // Clean up temp container and all created elements if they exist
    const tempContainer = document.querySelector('.temp-pdf-container')
    if (tempContainer && tempContainer.parentNode) {
      try {
        // Remove all children first to ensure canvases are cleaned up
        while (tempContainer.firstChild) {
          const child = tempContainer.firstChild
          // Clean up any canvases in the container
          if (child instanceof HTMLCanvasElement) {
            const ctx = child.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, child.width, child.height)
            }
            child.width = 0
            child.height = 0
          }
          tempContainer.removeChild(child)
        }
        document.body.removeChild(tempContainer)
      } catch (cleanupErr) {
        console.warn('Error cleaning up temp container:', cleanupErr)
      }
    }
    
    // Clean up any created elements that might still exist (only if in scope)
    // eslint-disable-next-line no-undef
    if (typeof createdElements !== 'undefined' && createdElements.length > 0) {
      // eslint-disable-next-line no-undef
      createdElements.forEach(element => {
        try {
          if (element instanceof HTMLCanvasElement) {
            const ctx = element.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, element.width, element.height)
            }
            element.width = 0
            element.height = 0
          } else if (element instanceof HTMLImageElement) {
            element.src = ''
            element.onload = null
            element.onerror = null
          } else if (element instanceof FileReader) {
            element.onloadend = null
            element.onerror = null
          }
          if (element.parentNode) {
            element.parentNode.removeChild(element)
          }
        } catch (cleanupErr) {
          // Ignore cleanup errors
        }
      })
    }
  }
}


// eslint-disable-next-line no-unused-vars
export async function generateClientSidePDF(deps = {}) {
  const {
    afterSaveFunction,
    prescriptionData,
    printSettings,
    savePrescriptionData,
  } = deps
  try {
    await savePrescriptionData()
    
    // Get A4 dimensions from printSettings
    const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
    const heightOfA4Page = currentDpiData?.height || 1123
    const widthOfA4Page = currentDpiData?.width || 794
    
    // Get all prescription pages
    const allPages = document.querySelectorAll('.prescription-page')
    if (!allPages || allPages.length === 0) {
      toast.error('No prescription pages found to generate PDF', { autoClose: false })
      return
    }

    // Create a container for all pages
    const pdfContainer = document.createElement('div')
    pdfContainer.style.width = `${widthOfA4Page}px`
    pdfContainer.style.margin = '0'
    pdfContainer.style.padding = '0'
    pdfContainer.style.backgroundColor = '#fff'
    pdfContainer.style.display = 'block'
    pdfContainer.style.position = 'relative'
    
    // Helper function to convert image to base64 - prioritizes already-loaded images
    const imageToBase64 = async (url, originalImgElement = null) => {
      // Strategy 1: Use already-loaded image from DOM if available and safe
      if (originalImgElement && originalImgElement.complete && originalImgElement.naturalHeight !== 0) {
        try {
          // Check if image is from same origin or was loaded with CORS
          const imgUrl = new URL(originalImgElement.src, window.location.href)
          const isSameOrigin = imgUrl.origin === window.location.origin
          
          // Try to get from canvas if same origin or if crossOrigin was set
          if (isSameOrigin || originalImgElement.crossOrigin !== null) {
            const canvas = document.createElement('canvas')
            canvas.width = originalImgElement.naturalWidth || originalImgElement.width
            canvas.height = originalImgElement.naturalHeight || originalImgElement.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(originalImgElement, 0, 0)
            return canvas.toDataURL('image/png')
          }
        } catch (e) {
          // Canvas might be tainted, continue to other methods
          console.warn('Could not get image from DOM element (tainted canvas), trying other methods:', url)
        }
      }
      
      // Strategy 2: Fetch with CORS headers (respects server CORS settings)
      try {
        const response = await fetch(url, { 
          mode: 'cors', 
          credentials: 'omit',
          headers: {
            'Accept': 'image/*',
          }
        })
        if (response.ok) {
          const blob = await response.blob()
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.onerror = () => reject(new Error('FileReader failed'))
            reader.readAsDataURL(blob)
          })
        }
      } catch (fetchError) {
        // Fetch failed, try Image element approach
        console.warn('Fetch with CORS failed, trying Image element:', url, fetchError.message)
      }
      
      // Strategy 3: Use Image element with same crossOrigin as original (if available)
      return new Promise((resolve) => {
        const img = new Image()
        // Use same crossOrigin setting as original image if available
        if (originalImgElement && originalImgElement.crossOrigin !== null) {
          img.crossOrigin = originalImgElement.crossOrigin
        } else {
          // Default to anonymous for cross-origin images
          try {
            const imgUrl = new URL(url, window.location.href)
            if (imgUrl.origin !== window.location.origin) {
              img.crossOrigin = 'anonymous'
            }
          } catch (e) {
            // URL parsing failed, try anonymous anyway
            img.crossOrigin = 'anonymous'
          }
        }
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            const dataURL = canvas.toDataURL('image/png')
            resolve(dataURL)
          } catch (e) {
            // Canvas is tainted, return original URL (html2canvas will handle with allowTaint)
            console.warn('Canvas tainted after Image load, will use original URL with allowTaint:', url)
            resolve(url)
          }
        }
        img.onerror = () => {
          // Image failed to load, return original URL
          console.warn('Image load failed, will use original URL:', url)
          resolve(url)
        }
        img.src = url
      })
    }

    // Clone each page and add to container
    for (let idx = 0; idx < allPages.length; idx++) {
      const page = allPages[idx]
      const clonedPage = page.cloneNode(true)
      
      // Remove UI-only elements
      const hideElements = clonedPage.querySelectorAll('.hide-on-print-only, .hide-on-print')
      hideElements.forEach(el => el.remove())
      
      // Convert all images to base64 - prioritize already-loaded images
      const images = clonedPage.querySelectorAll('img')
      for (const clonedImg of images) {
        const src = clonedImg.getAttribute('src')
        if (src && !src.startsWith('data:')) {
          try {
            // Try to find the original image element in the DOM (more robust search)
            let originalImg = null
            try {
              // First try exact src match
              originalImg = page.querySelector(`img[src="${src}"]`)
              if (!originalImg) {
                // Try finding by comparing src (handles relative/absolute URL differences)
                const allImgs = Array.from(page.querySelectorAll('img'))
                originalImg = allImgs.find(img => {
                  try {
                    return img.src === src || 
                           img.getAttribute('src') === src ||
                           new URL(img.src, window.location.href).href === new URL(src, window.location.href).href
                  } catch (e) {
                    return img.src === src || img.getAttribute('src') === src
                  }
                })
              }
            } catch (e) {
              console.warn('Error finding original image element:', e)
            }
            
            const base64 = await imageToBase64(src, originalImg)
            if (base64 && base64.startsWith('data:')) {
              clonedImg.src = base64
            } else {
              // If conversion failed, keep original URL - browser may still be able to print it
              console.warn('Image conversion returned non-base64, keeping original URL:', src)
              // Don't remove - keep the original src for browser printing
            }
          } catch (error) {
            console.error('Failed to convert image to base64:', src, error)
            // Keep original URL instead of removing - browser might still load it for printing
            console.warn('Keeping original image URL despite error:', src)
          }
        }
      }
      
      // Also handle background images in style attributes
      const elementsWithBgImages = clonedPage.querySelectorAll('[style*="background-image"]')
      for (const el of elementsWithBgImages) {
        const style = el.getAttribute('style')
        const bgMatch = style.match(/background-image:\s*url\(['"]?([^'"]+)['"]?\)/)
        if (bgMatch && bgMatch[1] && !bgMatch[1].startsWith('data:')) {
          try {
            const base64 = await imageToBase64(bgMatch[1])
            el.style.backgroundImage = `url(${base64})`
          } catch (error) {
            console.warn('Failed to convert background image to base64:', bgMatch[1], error)
          }
        }
      }
      
      // Ensure page has correct styling for PDF - but don't constrain width
      // Let content determine the width, then we'll scale it
      clonedPage.style.width = 'auto'
      clonedPage.style.minWidth = `${widthOfA4Page}px`
      clonedPage.style.maxWidth = 'none'
      clonedPage.style.height = 'auto' // Let content determine height
      clonedPage.style.minHeight = `${heightOfA4Page}px`
      clonedPage.style.maxHeight = 'none' // Don't constrain height - allow overflow
      clonedPage.style.margin = '0'
      clonedPage.style.marginBottom = '0'
      clonedPage.style.padding = '0'
      clonedPage.style.pageBreakAfter = idx < allPages.length - 1 ? 'always' : 'auto'
      clonedPage.style.pageBreakBefore = idx === 0 ? 'auto' : 'always'
      clonedPage.style.pageBreakInside = 'avoid'
      clonedPage.style.display = 'block'
      clonedPage.style.position = 'relative'
      clonedPage.style.boxSizing = 'border-box'
      clonedPage.style.overflow = 'visible'
      
      // Remove width constraints from all children to allow full width
      const allChildren = clonedPage.querySelectorAll('*')
      allChildren.forEach(child => {
        const childStyle = window.getComputedStyle(child)
        // If child has max-width constraint, remove it for PDF
        if (childStyle.maxWidth && childStyle.maxWidth !== 'none') {
          child.style.maxWidth = 'none'
        }
        // Ensure child can expand to full width if needed
        if (child.scrollWidth > widthOfA4Page) {
          child.style.width = 'auto'
          child.style.maxWidth = 'none'
        }
      })
      
      // Add a data attribute for page break detection
      clonedPage.setAttribute('data-pdf-page', idx + 1)
      
      pdfContainer.appendChild(clonedPage)
    }

    // Create a temporary container for PDF generation (off-screen)
    const tempContainer = document.createElement('div')
    tempContainer.style.position = 'absolute'
    tempContainer.style.left = '-9999px'
    tempContainer.style.top = '0'
    tempContainer.style.width = `${widthOfA4Page}px`
    tempContainer.style.backgroundColor = '#fff'
    tempContainer.appendChild(pdfContainer)
    document.body.appendChild(tempContainer)

    // No delay needed - pages are already rendered
    // Configure html2pdf options
    const _opt = {
      margin: [0, 0, 0, 0],
      filename: `Prescription_${prescriptionData?.prescriptionData?.patientName || 'Patient'}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        allowTaint: true, // Allow tainted canvas - we've converted images to base64
        backgroundColor: '#ffffff',
        width: widthOfA4Page,
        height: heightOfA4Page * allPages.length,
        proxy: undefined, // Don't use proxy
      },
      jsPDF: { 
        unit: 'px', 
        format: [widthOfA4Page, heightOfA4Page],
        orientation: 'portrait',
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.prescription-page',
        after: '.prescription-page',
        avoid: ['.prescription-page']
      }
    }

    toast.info('Converting images and generating PDF...', { autoClose: 2000 })
    
    // Images are already converted to base64, no delay needed
    // Generate PDF page by page to ensure all pages are included
    const { jsPDF } = await import('jspdf')
    // Use 'a4' format instead of custom pixel dimensions to avoid scaling issues
    const pdf = new jsPDF({
      unit: 'px',
      format: 'a4',
      orientation: 'portrait',
      compress: true,
    })
    
    // Generate each page separately and add to PDF
    let pdfPageCount = 0
    for (let idx = 0; idx < allPages.length; idx++) {
      const pageElement = pdfContainer.children[idx]
      if (!pageElement) continue
      
      // Check if page has actual content (not just whitespace)
      const hasContent = pageElement.textContent.trim().length > 0 || 
                        pageElement.querySelectorAll('img, canvas, svg').length > 0
      
      if (!hasContent) {
        console.log(`Skipping empty page ${idx + 1}`)
        continue
      }
      
      // Add new page (except for the very first page added)
      if (pdfPageCount > 0) {
        pdf.addPage()
      }
      pdfPageCount++
      
      // Use html2canvas to convert page to canvas
      // html2pdf.js includes html2canvas as a dependency
      const html2canvas = (await import('html2canvas')).default
      // Minimal delay - pages are already rendered
      // await new Promise(resolve => setTimeout(resolve, 100))
      
      // OPTIMIZATION: Batch all layout reads together to avoid forced reflows
      // Read all layout properties in one batch before any writes
      const pageRect = pageElement.getBoundingClientRect()
      const scrollWidth = pageElement.scrollWidth || pageRect.width
      const scrollHeight = pageElement.scrollHeight || pageRect.height
      
      // Batch child measurements - collect all children first, then measure all at once
      const allChildren = Array.from(pageElement.querySelectorAll('*'))
      const childMeasurements = allChildren.map(child => {
        const childRect = child.getBoundingClientRect()
        return {
          scrollWidth: child.scrollWidth || childRect.width,
          width: childRect.width
        }
      })
      
      // Find max width from cached measurements (no additional layout reads)
      let maxChildWidth = scrollWidth
      childMeasurements.forEach(measurement => {
        if (measurement.scrollWidth > maxChildWidth) {
          maxChildWidth = measurement.scrollWidth
        }
      })
      
      // Use the maximum width to ensure we capture everything
      const captureWidth = Math.max(maxChildWidth, scrollWidth, widthOfA4Page)
      // Use scrollHeight to capture all content, but cap at A4 height to avoid capturing beyond page bounds
      // This ensures we capture all content including boxes at the bottom
      const _captureHeight = Math.min(scrollHeight, heightOfA4Page)
      
      // Debug logging (disabled to reduce console noise)
      // console.log(`[PDF] Page ${idx + 1} - scrollWidth: ${scrollWidth}, maxChildWidth: ${maxChildWidth}, widthOfA4Page: ${widthOfA4Page}, captureWidth: ${captureWidth}`)
      
      // Temporarily set the page element width to captureWidth to ensure full capture
      const originalWidth = pageElement.style.width
      const originalMaxWidth = pageElement.style.maxWidth
      pageElement.style.width = `${captureWidth}px`
      pageElement.style.maxWidth = 'none'
      
      try {
        // Capture the full content including any overflow
        // Get canvas scale from printSettings, default to 1.0 (sufficient for print, reduces file size)
        const canvasScale = printSettings?.canvasScale || 1.0
        const canvas = await html2canvas(pageElement, {
          scale: canvasScale,
          useCORS: false, // Don't re-fetch images, use already loaded ones
          logging: false,
          letterRendering: true,
          allowTaint: false, // Don't allow taint - images are already base64
          backgroundColor: '#ffffff',
          width: captureWidth,
          height: scrollHeight, // Use scrollHeight to capture all content including boxes
          windowWidth: captureWidth,
          windowHeight: scrollHeight, // Use scrollHeight to ensure all content is captured
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
          removeContainer: false,
          onclone: (clonedDoc) => {
            // Ensure all elements are visible in the cloned document
            const clonedPage = clonedDoc.querySelector('.prescription-page')
            if (clonedPage) {
              // Make sure flex containers and boxes are visible
              const flexContainers = clonedPage.querySelectorAll('[style*="flex"]')
              flexContainers.forEach(container => {
                if (container.style.display === 'none') {
                  container.style.display = ''
                }
              })
              // Ensure continued boxes are visible
              const continuedBoxes = clonedPage.querySelectorAll('[id*="continued-box"]')
              continuedBoxes.forEach(box => {
                box.style.display = 'flex'
                box.style.visibility = 'visible'
                box.style.opacity = '1'
              })
            }
          },
        })
        
        // Restore original width
        pageElement.style.width = originalWidth
        pageElement.style.maxWidth = originalMaxWidth
        
        // Calculate actual dimensions (divide by canvasScale to get logical dimensions)
        const canvasActualWidth = canvas.width / canvasScale
        const canvasActualHeight = canvas.height / canvasScale
        
        // Get the actual PDF page dimensions (jsPDF converts a4 to pixels internally)
        const pdfPageWidth = pdf.internal.pageSize.getWidth()
        const pdfPageHeight = pdf.internal.pageSize.getHeight()
        
        // Debug logging (disabled to reduce console noise)
        // console.log(`[PDF] Page ${idx + 1} - canvasActualWidth: ${canvasActualWidth}, canvasActualHeight: ${canvasActualHeight}, pdfPageWidth: ${pdfPageWidth}, pdfPageHeight: ${pdfPageHeight}`)
        
        // Scale to fit PDF page dimensions exactly
        const scaleX = pdfPageWidth / canvasActualWidth
        const scaleY = pdfPageHeight / canvasActualHeight
        // Use the smaller scale to ensure it fits both dimensions
        const scale = Math.min(scaleX, scaleY)
        
        // Final dimensions for PDF
        const finalWidth = canvasActualWidth * scale
        const finalHeight = canvasActualHeight * scale
        
        // Add canvas to PDF - scale to fit A4 width
        // Optimized quality (0.85) for good balance between file size and print quality
        // 0.85 is still excellent for print while significantly reducing file size
        const imgData = canvas.toDataURL('image/jpeg', 0.85)
        pdf.addImage(imgData, 'JPEG', 0, 0, finalWidth, finalHeight, undefined, 'FAST')
        
        // Note: Don't add extra footer text here - prescription already has its own footer
        // Adding text on top would obscure the prescription footer
      } catch (error) {
        // Restore original width even if there's an error
        pageElement.style.width = originalWidth
        pageElement.style.maxWidth = originalMaxWidth
        throw error
      }
    }
    
    // Save the PDF
    pdf.save(`Prescription_${prescriptionData?.prescriptionData?.patientName || 'Patient'}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`)
    
    // Cleanup
    document.body.removeChild(tempContainer)
    
    toast.success('PDF generated successfully!', { autoClose: 2000 })
  } catch (error) {
    console.error('Error generating client-side PDF:', error)
    toast.error('Failed to generate PDF. Please try again.', { autoClose: false })
  }
  
  /* COMMENTED OUT - Original complex implementation to be fixed later
  // If specific pages are selected, hide non-selected pages before printing
  if (selectedPages && selectedPages.length > 0) {
    // Sort selected pages to ensure correct order
    const sortedPages = [...selectedPages].sort((a, b) => a - b)
    
    // Remove any existing page selection style first
    const styleId = 'page-selection-print-style'
    let existingStyle = document.getElementById(styleId)
    if (existingStyle) {
      existingStyle.remove()
    }
    
    // Verify pages exist in DOM and add/remove classes directly
    const allPageElements = document.querySelectorAll('.prescription-page')
    
    // First, add a class to hide all pages in print
    allPageElements.forEach((el) => {
      el.classList.add('hide-page-in-print')
    })
    
    // Then, remove the hide class from selected pages
    sortedPages.forEach((pageNum) => {
      const pageElement = document.querySelector(
        `.prescription-page[data-page-number="${pageNum}"]`
      )
      if (pageElement) {
        pageElement.classList.remove('hide-page-in-print')
        console.log(`Page ${pageNum} will be printed`)
      } else {
        console.warn(`Page ${pageNum} not found in DOM`)
      }
    })
    
    // Add CSS rule for the hide class
    const styleElement = document.createElement('style')
    styleElement.id = styleId
    styleElement.textContent = `
      @media print {
        .prescription-page.hide-page-in-print {
          display: none !important;
        }
      }
    `
    
    document.head.appendChild(styleElement)
    
    // OPTIMIZATION: Use requestAnimationFrame instead of forced reflow
    // This batches the style recalculation with the browser's render cycle
    // and avoids the forced reflow warning
    requestAnimationFrame(() => {
      // Styles will be recalculated naturally in the next frame
      // No need for explicit forced reflow
    })
    
    // Clean up after printing
    const cleanup = () => {
      setTimeout(() => {
        // Remove the style element
        const styleToRemove = document.getElementById(styleId)
        if (styleToRemove && styleToRemove.parentNode) {
          styleToRemove.parentNode.removeChild(styleToRemove)
        }
        // Remove hide classes from all pages
        const allPages = document.querySelectorAll('.prescription-page')
        allPages.forEach((el) => {
          el.classList.remove('hide-page-in-print')
        })
      }, 1000)
    }
    
    window.addEventListener('afterprint', cleanup, { once: true })
  } else {
    // Remove any existing page selection style if printing all pages
    const existingStyle = document.getElementById('page-selection-print-style')
    if (existingStyle) {
      existingStyle.remove()
    }
    // Remove hide classes from all pages
    const allPages = document.querySelectorAll('.prescription-page')
    allPages.forEach((el) => {
      el.classList.remove('hide-page-in-print')
    })
  }
  
  window.scrollTo({
    top: 0,
    behavior: 'instant',
  })
  
  // Small delay to ensure styles are applied before print dialog opens
  setTimeout(() => {
    // Add print-specific meta tag to prevent scaling
    const existingMeta = document.querySelector('meta[name="viewport"]')
    if (existingMeta) {
      existingMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no')
    }
    
    // Debug: Log all pages in DOM
    const allPages = document.querySelectorAll('.prescription-page')
    console.log(`Total pages found in DOM: ${allPages.length}`)
    allPages.forEach((page, idx) => {
      const pageNum = page.getAttribute('data-page-number')
      const isHidden = page.classList.contains('hide-page-in-print')
      console.log(`Page ${pageNum}: hidden=${isHidden}, display=${window.getComputedStyle(page).display}`)
    })
    
    // Ensure all pages are visible before printing
    allPages.forEach((page) => {
      if (!page.classList.contains('hide-page-in-print')) {
        page.style.display = 'block'
        page.style.visibility = 'visible'
        page.style.opacity = '1'
      }
    })
    
    window.print()
  }, 100)
  END OF COMMENTED CODE */
}

async function _saveTriggerFunction() {
  if (!triggerSave) {
    await savePrescriptionData()
    if (setTriggerSave) {
      setTriggerSave()
    }
    if (afterSaveFunction) {
      afterSaveFunction(triggerSave === TRIGGER_SAVE_COMPLETED)
    }
  }
}

// function openInvestigationForm(inputValue = '') {
//   const investigationObj = {
//     ...INVESTIGATION_OBJECT,
//     title: inputValue,
//   }

//   setInvestigationToEdit(investigationObj)
//   setInitialInvestigationObjectToEdit(investigationObj)
//   setShowInvestigationForm(true)
// }

// async function getInvestigationSamples() {
//   const response = await getMPDataWithoutParams('/api/secure/clinic-practitioner/investigation-sample/list')
//   if (response?.status === 200) {
//     setSampleTypes(response?.data)
//   }
// }

